import { allTrackers } from "app/services/track/track";
import type { track as TrackType } from "pro-features/components/WithTrack/src/context";

import { getCurrentPageUrl } from "./helpers/onboardingAgentHelpers";

type TrackingAttributeValue = string | number | boolean;
type TrackingAttributes = Record<string, TrackingAttributeValue>;

export type OnboardingAgentFeedbackType = "like" | "dislike" | "dislike_submit";

export type OnboardingAgentSessionEndedParams = {
    readonly queriesCount: number;
    readonly sessionDurationMs: number;
    readonly conversationPrint: string;
    readonly threadId?: string;
};

type OnboardingAgentBaseMessageParams = {
    readonly threadId?: string;
};

type OnboardingAgentUserReplyParams = OnboardingAgentBaseMessageParams & {
    readonly userReply: string;
};

type OnboardingAgentAgentReplyParams = OnboardingAgentBaseMessageParams & {
    readonly agentReply: string;
    readonly agentQuickLinks: string;
};

type OnboardingAgentQuickLinksClickParams = OnboardingAgentBaseMessageParams & {
    readonly clickedQuickLinks: string;
};

type OnboardingAgentDirectLinkDetectedParams = OnboardingAgentBaseMessageParams & {
    readonly detectedDomain: string;
};

type OnboardingAgentFeedbackParams = OnboardingAgentBaseMessageParams & {
    readonly type: OnboardingAgentFeedbackType;
    readonly messageIndex: number;
    readonly comment?: string;
};

type OnboardingAgentCopyParams = OnboardingAgentBaseMessageParams & {
    readonly messageIndex: number;
    readonly failed?: boolean;
};

type OnboardingAgentErrorParams = OnboardingAgentBaseMessageParams & {
    readonly errorMessage: string;
};

type OnboardingAgentPromotionClickParams = {
    readonly clickedText: string;
    /** 1-based position among the options shown, e.g. `1` of 4. */
    readonly optionIndex: number;
    readonly optionCount: number;
};

const TRACKING_EVENT_NAME = "Onboarding agent";
const CATEGORY_BUTTON = "Button";
const CATEGORY_POPUP = "popup";
const ACTION_OPEN = "Open";
const ACTION_INTERACTION = "Interaction";
const ACTION_CLOSE = "Close";
const ACTION_CLICK = "Click";
const ACTION_EXIST = "Exist";

/** Fixed name shared by the nudge popup's OA13-OA15 events — distinguished by `action`, not by name. */
const PROMOTION_NAME = "Onboarding agent promotion";

const FEEDBACK_POSITIVE = "positive feedback";
const FEEDBACK_NEGATIVE = "Negative feedback";

const SUB_SUB_NAME_SEPARATOR = "; ";

const buildSubSubName = (threadId: string | undefined, ...messageParts: ReadonlyArray<string | undefined>): string =>
    [`threadId: ${threadId}`, `Time stamp: ${new Date().toISOString()}`, ...messageParts]
        .filter((part): part is string => Boolean(part && part.length > 0))
        .join(SUB_SUB_NAME_SEPARATOR);

const createTrackingName = (eventSubName?: string | undefined, eventSubSubName?: string | undefined): string =>
    [TRACKING_EVENT_NAME, eventSubName, eventSubSubName].filter(Boolean).join("/");

const createBaseAttributes = (): TrackingAttributes => ({
    hostname: window.location.hostname,
    page: getCurrentPageUrl(),
});

const createTrackingService = (track: TrackType) => {
    let hasTrackedExist = false;

    const trackWithContext = (
        category: string,
        action: string,
        name: string,
        attributes: TrackingAttributes = {},
    ): void => {
        track(category, action, name, {
            ...createBaseAttributes(),
            ...attributes,
        });
    };

    return {
        trackAgentExist(): void {
            if (hasTrackedExist) {
                return;
            }
            hasTrackedExist = true;
            trackWithContext(CATEGORY_BUTTON, ACTION_EXIST, createTrackingName());
        },

        trackChatbotOpen(threadId?: string): void {
            trackWithContext(CATEGORY_BUTTON, ACTION_OPEN, createTrackingName(undefined, buildSubSubName(threadId)));
        },

        trackChatbotClose(reason: string, threadId?: string): void {
            trackWithContext(
                CATEGORY_BUTTON,
                ACTION_CLOSE,
                createTrackingName(undefined, buildSubSubName(threadId, `reason: ${reason}`)),
            );
        },

        trackExpand(expanded: boolean, threadId?: string): void {
            trackWithContext(
                CATEGORY_BUTTON,
                ACTION_INTERACTION,
                createTrackingName("Expand", buildSubSubName(threadId, `expanded: ${expanded}`)),
            );
        },

        trackUserReply({ threadId, userReply }: OnboardingAgentUserReplyParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName("User reply", buildSubSubName(threadId, `user reply: ${userReply}`)),
            );
        },

        trackAgentReply({ threadId, agentReply, agentQuickLinks }: OnboardingAgentAgentReplyParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName(
                    "Agent reply",
                    buildSubSubName(
                        threadId,
                        agentQuickLinks ? `Agent quick links: ${agentQuickLinks}` : undefined,
                        `Agent reply: ${agentReply}`,
                    ),
                ),
            );
        },

        trackQuickLinksClick({ threadId, clickedQuickLinks }: OnboardingAgentQuickLinksClickParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName(
                    "Quick links click",
                    buildSubSubName(threadId, `clicked quick links: ${clickedQuickLinks}`),
                ),
            );
        },

        trackDirectLinkDetected({ threadId, detectedDomain }: OnboardingAgentDirectLinkDetectedParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName(
                    "Direct link detected",
                    buildSubSubName(threadId, `detected domain: ${detectedDomain}`),
                ),
            );
        },

        trackAssistantLinkClick(label: string, href: string, threadId?: string): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName(
                    "Assistant link click",
                    buildSubSubName(threadId, `clicked assistant link: ${label}`, `href: ${href}`),
                ),
            );
        },

        trackFeedback({ type, messageIndex, comment, threadId }: OnboardingAgentFeedbackParams): void {
            if (type === "dislike_submit") {
                trackWithContext(
                    CATEGORY_POPUP,
                    ACTION_INTERACTION,
                    createTrackingName(
                        "Feedback negative",
                        buildSubSubName(threadId, `${FEEDBACK_NEGATIVE}: ${comment ?? ""}`),
                    ),
                    {
                        message_index: messageIndex,
                        comment_length: comment ? comment.trim().length : 0,
                    },
                );
                return;
            }

            const feedbackLabel = type === "like" ? FEEDBACK_POSITIVE : FEEDBACK_NEGATIVE;
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName("Feedback", buildSubSubName(threadId, feedbackLabel)),
                { message_index: messageIndex },
            );
        },

        trackCopy({ messageIndex, failed = false, threadId }: OnboardingAgentCopyParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName("Copy", buildSubSubName(threadId, failed ? "failed" : undefined)),
                { message_index: messageIndex },
            );
        },

        trackLimitReached({ errorMessage, threadId }: OnboardingAgentErrorParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName("Limit reached", buildSubSubName(threadId, `error: ${errorMessage}`)),
            );
        },

        trackErrorShown({ errorMessage, threadId }: OnboardingAgentErrorParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_INTERACTION,
                createTrackingName("Error shown", buildSubSubName(threadId, `error: ${errorMessage}`)),
            );
        },

        trackStopRequest(threadId?: string): void {
            trackWithContext(
                CATEGORY_BUTTON,
                ACTION_INTERACTION,
                createTrackingName("Stop request", buildSubSubName(threadId)),
            );
        },

        trackNewChat(threadId?: string): void {
            trackWithContext(
                CATEGORY_BUTTON,
                ACTION_INTERACTION,
                createTrackingName("New chat", buildSubSubName(threadId)),
            );
        },

        trackSessionEnded({
            queriesCount,
            sessionDurationMs,
            conversationPrint,
            threadId,
        }: OnboardingAgentSessionEndedParams): void {
            trackWithContext(
                CATEGORY_POPUP,
                ACTION_CLOSE,
                createTrackingName("Session ended", buildSubSubName(threadId)),
                {
                    conversation_print: conversationPrint,
                    queries_count: queriesCount,
                    session_duration_ms: sessionDurationMs,
                },
            );
        },

        // OA13
        trackNudgePromotionOpen(): void {
            trackWithContext(CATEGORY_POPUP, ACTION_OPEN, PROMOTION_NAME);
        },

        // OA14
        trackNudgePromotionClose(): void {
            trackWithContext(CATEGORY_POPUP, ACTION_CLOSE, PROMOTION_NAME);
        },

        // OA15
        trackNudgePromotionClick({ clickedText, optionIndex, optionCount }: OnboardingAgentPromotionClickParams): void {
            trackWithContext(CATEGORY_POPUP, ACTION_CLICK, PROMOTION_NAME, {
                clicked_text: clickedText,
                location: `${optionIndex}/${optionCount}`,
            });
        },
    };
};

export const onboardingAgentTrackingService = createTrackingService(allTrackers.dispatchTrackEvent.bind(allTrackers));
